import React, { useState, useEffect, useCallback, useMemo} from 'react';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import CustomTablePagination from 'src/shared/TablePagination/CustomTablePagination';
import { GridContainer, Grid } from 'src/shared/grid';
import Autocomplete from '@mui/material/Autocomplete';
import { Box, Button, TableSortLabel, TextField, Tooltip } from '@mui/material';
import { errorResponse, validateCharacters, ValidCharacters} from 'src/store/common';
import 'src/features/data-import/DataImport/DataImport.scss';
import { StyledTableCell, StyledTableRow } from 'src/shared/Styles/Styles';
import { useStoreContext } from 'src/store/context';
import { useAddAlert, useAllStations, useUser } from 'src/store/actions';
import RefreshIcon from '@mui/icons-material/Refresh';
import LoadingSpinner from 'src/shared/loading/loading';
import { alertMessages } from 'src/constants';
import { getStatusRecalc } from 'src/apis/getNfdrsCalls';
import { visuallyHidden } from '@mui/utils';
import moment from 'moment';
import { getDeadCatalogsCall, getDroughtCatalogCall, getFuelCatalogs, getLiveCatalogCall } from 'src/apis/getCatalogCalls';
import { getStations } from 'src/apis/getWeatherCalls';
import localforage from 'localforage';
import { Station } from 'src/types';


interface Data {
  stationName: string;
  fuelModelCatalogName: string;
  droughtCatalogName: string;
  liveCatalogName: string;
  deadCatalogName: string;
  comments: string;
  status: string;
  modifiedTime: string;
  modifiedBy: string;
}


type Order = 'asc' | 'desc';

interface HeadCell {
  disablePadding: boolean;
  id: keyof Data;
  label: string;
  numeric: boolean;
}

const headCells: readonly HeadCell[] = [
  {
    id: 'stationName',
    numeric: false,
    disablePadding: false,
    label: 'STATION NAME',
  },
  {
    id: 'fuelModelCatalogName',
    numeric: false,
    disablePadding: false,
    label: 'FUEL MODEL CATALOG NAME',
  },
  {
    id: 'droughtCatalogName',
    numeric: false,
    disablePadding: false,
    label: 'DROUGHT CATALOG NAME',
  },
  {
    id: 'liveCatalogName',
    numeric: false,
    disablePadding: false,
    label: 'LIVE CATALOG NAME',
  },
  {
    id: 'deadCatalogName',
    numeric: false,
    disablePadding: false,
    label: 'DEAD CATALOG NAME',
  },
  {
    id: 'modifiedTime',
    numeric: false,
    disablePadding: false,
    label: 'DATE',
  },
  {
    id: 'status',
    numeric: false,
    disablePadding: false,
    label: 'STATUS',
  },
  {
    id: 'modifiedBy',
    numeric: false,
    disablePadding: false,
    label: 'UPDATED BY',
  },
];

interface EnhancedTableProps {
  onRequestSort: (event: React.MouseEvent<unknown>, property: keyof Data) => void;
  order: Order;
  orderBy: string;
}

/*SPECIAL SORTABLE HEADER*/
function EnhancedTableHead(props: Readonly <EnhancedTableProps>) {
  const { order, orderBy, onRequestSort } = props;
  const createSortHandler = (property: keyof Data) => (event: React.MouseEvent<unknown>) => {
      onRequestSort(event, property);
  };
  return (
    <TableHead>
      <TableRow>
        {headCells.map((headCell, index) => (
          (
            <TableCell
              className='headerParent'
              style={{backgroundColor: '#d9d9d9', width: '20%!important'}}
              key={headCell.id}
              tabIndex={0}
              align={'left'}
              padding={headCell.disablePadding ? 'none' : 'normal'}
              sortDirection={orderBy === headCell.id ? order : false}
            >
              {
                <TableSortLabel
                  active={orderBy === headCell.id}
                  direction={orderBy === headCell.id ? order : 'asc'}
                  onClick={createSortHandler(headCell.id)}
                >
                  {headCell.label}
                  {/* Here we add the sort option + sort arrow */}
                  {orderBy === headCell.id ? (
                    <Box component="span" sx={visuallyHidden}>
                      {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                    </Box>
                  ) : <Box component="span" sx={visuallyHidden}></Box>}

                </TableSortLabel>

              }
            </TableCell>
          )
        ))}
      </TableRow>
    </TableHead>
  );
}

type DataRecalcStatusProps={
}

export const DataRecalcStatus: React.FC<DataRecalcStatusProps>= () => {
  const [state, dispatch] = useStoreContext();
  const setUser = useUser(dispatch);
  const [token, setToken] = useState<string|null>(null);
  const [order, setOrder] = useState<Order>('asc');
  const [orderBy, setOrderBy] = useState<keyof Data>('stationName');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const addAlert = useAddAlert(dispatch);
  const allStoredStations = useAllStations(dispatch);

  const fuelObj =useMemo(()=>({fireDangerModelName: 'All'}),[]);
  const [fuelList, setFuelList] = useState<any[]>([fuelObj]);
  const [fuelCatalog, setFuelCatalog] = useState<string|undefined>('All');
  const [fuelCatalogInput, setFuelCatalogInput] = useState('');

  const droughtObj =useMemo(()=>({name: 'All'}),[]);
  const [droughtList, setDroughtList] = useState<any[]>([droughtObj]);
  const [droughtCatalog, setDroughtCatalog] = useState<string|undefined>('All');
  const [droughtCatalogInput, setDroughtCatalogInput] = useState('');
  
  const liveObj =useMemo(()=>({catalogName: 'All'}),[]);
  const [liveList, setLiveList] = useState<any[]>([liveObj]);
  const [liveCatalog, setLiveCatalog] = useState<string|undefined>('All');
  const [liveCatalogInput, setLiveCatalogInput] = useState('');

  const deadObj =useMemo(()=>({catalogName: 'All'}),[]);
  const [deadList, setDeadList] = useState<any[]>([deadObj]);
  const [deadCatalog, setDeadCatalog] = useState<string|undefined>('All');
  const [deadCatalogInput, setDeadCatalogInput] = useState('');

  const [stationSearched, setStationSearched] = useState<any>(null);
  const [recalcStatuses, setRecalcStatuses] = useState<any[]>([]);

  const [loading, setLoading] = useState<boolean>(false);

  /**Make a call to populate the table with the bulk data job files */
  const recalcStatusResponse = useCallback((request: any) => {
    (async () => {
      try {
        setLoading(true);

        const response: any = await getStatusRecalc(request,addAlert)
        setTotalCount(response._metadata.total_count);
        setRecalcStatuses(response.data)
      } catch (error) {
        setRecalcStatuses([]);
        errorResponse(error, addAlert, alertMessages.getRecalcStatuses);
        console.log('error', error);
      }
      setLoading(false);
    })();
  }, [addAlert]); 

    /**Make a call to populate fuel model catalog list */
  const fuelModelCatalogList = useCallback((request: any) => {
    (async () => {
      try {
        const response: any = await getFuelCatalogs(request,addAlert);
        if(response){
          const allResponse = response.data;
          allResponse.unshift(fuelObj)
          setFuelList(allResponse)
        }
      } catch (error) {
        errorResponse(error, addAlert, alertMessages.getRecalcStatuses);
        console.log('error', error);
      }
    })();
  }, [addAlert, fuelObj]); 

      /**Make a call to populate drought catalog list */
  const droughtCatalogList = useCallback((request: any) => {
    (async () => {
      try {
        const response: any = await getDroughtCatalogCall(request,addAlert);
        if(response){
          const allResponse = response.data;
          allResponse.unshift(droughtObj)
          setDroughtList(allResponse)
        }
      } catch (error) {
        errorResponse(error, addAlert, alertMessages.getRecalcStatuses);
        console.log('error', error);
      }
    })();
  }, [addAlert, droughtObj]); 

/**Make a call to populate live catalog list */
  const liveCatalogList = useCallback((request: any) => {
    (async () => {
      try {
        const response: any = await getLiveCatalogCall(request,addAlert);
        if(response){
          const allResponse = response.data;
          allResponse.unshift(liveObj)
          setLiveList(allResponse)
        }
      } catch (error) {
        errorResponse(error, addAlert, alertMessages.getRecalcStatuses);
        console.log('error', error);
      }
    })();
  }, [addAlert, liveObj]); 

  /**Make a call to populate live catalog list */
  const deadCatalogList = useCallback((request: any) => {
    (async () => {
      try {
        const response: any = await getDeadCatalogsCall(request,addAlert);
        if(response){
          const allResponse = response.data;
          allResponse.unshift(deadObj)
          setDeadList(allResponse)
        }
      } catch (error) {
        errorResponse(error, addAlert, alertMessages.getRecalcStatuses);
        console.log('error', error);
      }
    })();
  }, [addAlert, deadObj]); 


    /*call incase no station list is available*/
    const stationListCallback = useCallback(() =>{
      (async()=>{
        try{
          const varRequest = {
            returnAll: true,		
          };
          const response: any = await getStations(varRequest, addAlert);
          if (response) {
            const stations = response.stations.sort(function (a: any, b: any) {
              return a.search_name.localeCompare(b.search_name);
            })
            allStoredStations(stations)
          }
        }
        catch(error){
          addAlert({
            id:'',
            message: alertMessages.retrievingStationData,
            type:'error-info',
            icon:'fa-exclamation-triangle'
          })
        }
      })();
    },[addAlert, allStoredStations, ]);
  
  const handleRefresh = useCallback(() => {
    const request = {
      stationName: stationSearched,
      fuelModelCatalogName: fuelCatalog?.includes('All')? '': fuelCatalog,
      droughtCatalogName: droughtCatalog?.includes('All')? '': droughtCatalog,
      liveCatalogName: liveCatalog?.includes('All')? '': liveCatalog,
      deadCatalogName: deadCatalog?.includes('All')? '': deadCatalog,
      sortOrder:order,
      sortBy:orderBy,	
      perPage:rowsPerPage,
      page: page,
    }
    recalcStatusResponse(request);
  },[ fuelCatalog, droughtCatalog, liveCatalog, deadCatalog,stationSearched,
    rowsPerPage, page, recalcStatusResponse, order, orderBy
  ]);

  useEffect(()=>{
    /** find access token and set to send to bearer */
    if(state.userTokens && token) {
      /** If the Token is refreshed this will set the @param token equal to updated value */
      setToken(state.userTokens.accessToken);
    }
    else if(localStorage.getItem('user') && state.userTokens && !token){
      /** find access token and set to send to bearer */
      const parsedSession = JSON.parse(localStorage.getItem('user')??'');
      setUser(parsedSession?.userInfo);
      setToken(state.userTokens.accessToken);
    }
    else if(!state.user){
      const parsedSession = JSON.parse(localStorage.getItem('user')??'')
      setUser(parsedSession?.userInfo)    
    }
  },[state.userTokens, setUser, setToken, state.user, token]);

  /**Populate the table */
  useMemo(() => {
    const request = {
      stationName: stationSearched,
      fuelModelCatalogName: fuelCatalog?.includes('All')? '': fuelCatalog,
      droughtCatalogName: droughtCatalog?.includes('All')? '': droughtCatalog,
      liveCatalogName: liveCatalog?.includes('All')? '': liveCatalog,
      deadCatalogName: deadCatalog?.includes('All')? '': deadCatalog,
      sortOrder:order,
      sortBy:orderBy,	
      perPage:  rowsPerPage,
      page: page,
    }
    recalcStatusResponse(request);

  },[recalcStatusResponse,liveCatalog, deadCatalog,fuelCatalog, droughtCatalog, stationSearched
     ,page, rowsPerPage, order, orderBy]);
    
  useMemo(()=>{
    
    const requestFuel = {
        id: null,
        catalogName: null,
        perPage: 10000,
        page: 0,
        sortBy: 'fire_danger_model_name',
        sortOrder: 'asc',
        fuelModel: 'ALL'
    }
    fuelModelCatalogList(requestFuel);

    const requestDrought={
        id: null,
        perPage: 10000,
        page: 0,
        sortBy: 'kbdi_parameter_catalog_name',
        sortOrder: 'asc',
    }
    droughtCatalogList(requestDrought);
    const requestLive = {
        id: null,
        sortBy: 'catalog_name',
        sortOrder: 'ASC'
    }
    liveCatalogList(requestLive);
    const requestDead = {
        id: null,
        perPage: 10000,
        page: 0,
        sortBy: 'dead_fuel_parameter_catalog_name',
        sortOrder: 'asc',
    };
    deadCatalogList(requestDead);
  }, [fuelModelCatalogList, droughtCatalogList, 
deadCatalogList, liveCatalogList,]);

  /** Check if the filter options are initialized */
  useEffect(()=>{
    if (state.all_stations?.length < 1) {
      localforage.getItem('allStations').then(async function (stations) {
        let allStations = stations as Station[];
        if (!allStations) {
          stationListCallback();
        }
        else {
          allStoredStations(allStations);
        }
      });
    }
  },[state.all_stations?.length,allStoredStations, stationListCallback]);


    const onStationSearch = (e:React.SyntheticEvent<Element, Event>, incomingVal: any) =>{
      if(incomingVal===null || incomingVal===undefined){
        setStationSearched(null)
      }
      if(incomingVal && state.all_stations?.length>1){
        const findStation = state.all_stations.find((station)=>station.search_name === incomingVal?.search_name);
        setStationSearched(findStation?.station_name)
      }
    }
  /**Auto refresh the table at a set interval of 45000 Miliseconds */
  useEffect(() => {
    const autoRefreshTable = setInterval(() => {
      handleRefresh();
    }, 45000);
    return () => {
      if(autoRefreshTable){
        clearInterval(autoRefreshTable);
      }
    }
  },[handleRefresh]);
  

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(Number.parseInt(event.target.value, 10));
    setPage(0);
  };
  
    const handleRequestSort = (event:React.MouseEvent<unknown>, property:keyof Data) => {
      const isAsc = orderBy === property && order === 'asc';
      setOrder(isAsc ? 'desc' : 'asc');
      setOrderBy(property);
      setPage(0);
        const request = {
          stationName: stationSearched,
          fuelModelCatalogName: fuelCatalog?.includes('All')? '': fuelCatalog,
          droughtCatalogName: droughtCatalog?.includes('All')? '': droughtCatalog,
          liveCatalogName: liveCatalog?.includes('All')? '': liveCatalog,
          deadCatalogName: deadCatalog?.includes('All')? '': deadCatalog,
          sortOrder: isAsc ? 'desc' : 'asc',
          sortBy: property,
          perPage:  rowsPerPage,
          page: page,
        }
        recalcStatusResponse(request);
    };
  
//selected might be where selection is placed
  return (
    <div style={{paddingBottom: '2.5%'}} className='table-page-container' data-testid={'table-page-container'}>

        <h1>Stations Recalculate</h1>
        <hr></hr>
          {/*DROP DOWNS AND FILTERS*/}
        <GridContainer className="margin-top-3 dropdown-style" style={{ paddingBottom:'2.5%'}}>
          <Grid row gap="md">
            <div style={{ width: '30%'}}>
              <label className="font-sans-md text-bold" htmlFor='catalogStationSearch'>Search All Stations</label>
            </div>
            <div style={{ width: '20%' }}>
              <label className="font-sans-md text-bold" htmlFor='fuelList'>Fuel Model Catalog</label>
            </div> 
            <div style={{ width: '15%' }}>
              <label className="font-sans-md text-bold" htmlFor='droughtList'>Drought Catalog</label>
            </div> 
            <div style={{ width: '15%' }}>
              <label className="font-sans-md text-bold" htmlFor='liveList'>Live Catalog</label>
            </div>
            <div style={{ width: '15%' }}>
              <label className="font-sans-md text-bold" htmlFor='deadList'>Dead Catalog</label>
            </div>
          </Grid>
          <Grid row gap="md" className="margin-top-1">

          <div style={{ width: '30%' }}>
            <div className='searchFiles'>
              <i className="fas fa-search" />
              <Autocomplete
                onChange={(event, newValue) => onStationSearch(event, newValue)}
                isOptionEqualToValue={(option,value)=>option?.search_name===value?.search_name}
                options={state.all_stations? state.all_stations:[]}
                getOptionLabel={(option) => `${option.search_name}`}
                filterOptions={(options, { inputValue }) => {
                  const search = inputValue.toLowerCase();
                  if (search.length <= 2) {
                    return options.filter((option) =>
                      option.state?.toLowerCase().includes(search) ||
                      option.station_id.toString().includes(search)

                    )
                  }
                  return options.filter((option) =>
                    option.station_name?.toLowerCase().includes(search) ||
                    option.network_name?.toLowerCase().includes(search) ||
                    option.state?.toLowerCase().includes(search) ||
                    option.station_id.toString().includes(search)
                  )
                }} 
                aria-labelledby='catalogStationSearch'
                id="catalogStationSearch"
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Search by Station"
                    InputProps={{
                      ...params.InputProps,
                      type: 'search',
                      style: { height: '5rem' },
                    }}
                    style={{ height: '3.5rem' }}

                  />
                )}
              />
            </div>
          </div>
         
          <div style={{ width: '20%' }}>
            <Autocomplete
              className="height-55"
              value={fuelCatalog}
              onChange={(event: any, newValue: any) => {
                setFuelCatalog(newValue);
                setPage(0);
              }}
              inputValue={fuelCatalogInput}
              onInputChange={(event, newInputValue) => {
                setFuelCatalogInput(newInputValue);
              }}
              id="fuelList"
              aria-labelledby='fuelList'
              disableClearable
              options={fuelList?.map((option) => option?.fireDangerModelName)}
              renderInput={(params) => (
                <TextField
                  onInput={(e) => validateCharacters(e, ValidCharacters.TextField)}
                  data-testid={'fuelList'}
                  {...params}
                  InputProps={{
                    ...params.InputProps,
                    type: 'search',
                  }}
                  InputLabelProps={{
                    shrink: true,
                    style: { visibility: 'hidden' }
                  }}
                />
              )}
            />
          </div> 

          <div style={{ width: '15%' }}>
            <Autocomplete
              className="height-55"
              value={droughtCatalog}
              onChange={(event: any, newValue: any) => {
                setDroughtCatalog(newValue);
                setPage(0);
              }}
              inputValue={droughtCatalogInput}
              onInputChange={(event, newInputValue) => {
                setDroughtCatalogInput(newInputValue);
              }}
              id="droughtList"
              aria-labelledby='droughtList'
              disableClearable
              options={droughtList?.map((option) => option?.name)}
              renderInput={(params) => (
                <TextField
                  onInput={(e) => validateCharacters(e, ValidCharacters.TextField)}
                  data-testid={'droughtList'}
                  {...params}
                  InputProps={{
                    ...params.InputProps,
                    type: 'search',
                  }}
                  InputLabelProps={{
                    shrink: true,
                    style: { visibility: 'hidden' }
                  }}
                />
              )}
            />
          </div> 

          <div style={{ width: '15%' }}>
            <Autocomplete
              className="height-55"
              value={liveCatalog}
              onChange={(event: any, newValue: any) => {
                setLiveCatalog(newValue);
                setPage(0);
              }}
              inputValue={liveCatalogInput}
              onInputChange={(event, newInputValue) => {
                setLiveCatalogInput(newInputValue);
              }}
              id="liveList"
              aria-labelledby='liveList'
              disableClearable
              options={liveList?.map((option) => option?.catalogName)}
              renderInput={(params) => (
                <TextField
                  onInput={(e) => validateCharacters(e, ValidCharacters.TextField)}
                  data-testid={'liveList'}
                  {...params}
                  InputProps={{
                    ...params.InputProps,
                    type: 'search',
                  }}
                  InputLabelProps={{
                    shrink: true,
                    style: { visibility: 'hidden' }
                  }}
                />
              )}
            />
          </div> 

            <div style={{ width: '15%' }}>
            <Autocomplete
              className="height-55"
              value={deadCatalog}
              onChange={(event: any, newValue: any) => {
                setDeadCatalog(newValue);
                setPage(0);
              }}
              inputValue={deadCatalogInput}
              onInputChange={(event, newInputValue) => {
                setDeadCatalogInput(newInputValue);
              }}
              id="deadList"
              aria-labelledby='deadList'
              disableClearable
              options={deadList?.map((option) => option?.catalogName)}
              renderInput={(params) => (
                <TextField
                  onInput={(e) => validateCharacters(e, ValidCharacters.TextField)}
                  data-testid={'deadList'}
                  {...params}
                  InputProps={{
                    ...params.InputProps,
                    type: 'search',
                  }}
                  InputLabelProps={{
                    shrink: true,
                    style: { visibility: 'hidden' }
                  }}
                />
              )}
            />
          </div> 
          <div style={{ width: '2%' }}>
            <Tooltip title={"Refresh Table"} placement="top-start">
                <Button variant="outlined" style={{height: '90%'}} onClick={handleRefresh}><RefreshIcon/></Button>
            </Tooltip>
          </div>

          </Grid>
        </GridContainer>

        <TableContainer component={Paper} className='margin-top-2 first-table MuiPaper-root-special'>
        {(loading) && (
          <div style={{height: '30%'}}>
          <LoadingSpinner/>
          </div>
        )}
        {/* TABLE FOR FUELS*/}
          <Table
            sx={{ minWidth: 750 }}
          >
            <EnhancedTableHead
              order={order}
              orderBy={orderBy}
              onRequestSort={handleRequestSort}
            />
             {recalcStatuses?.length===0&&
                <div style={{padding:'10px', fontSize:'18px', width:'400px'}}>No Data Available with the Selected Filters</div>}
            <TableBody>
              {recalcStatuses?.map((row, index) => {
                const labelId = `enhanced-table-checkbox-${index}`;

                return (
                  <StyledTableRow
                    tabIndex={-1}
                    key={`${row.stationName}-${row.fuelModelCatalogName}-${row.droughtCatalogName}-${row.liveCatalogName}-${row.deadCatalogName}-${index}`}
                    className='selectedHover'
                  >
                    <StyledTableCell className='text-align-override selectedHover'
                      component="th"
                      id={labelId}
                      scope="row"
                      tabIndex={0}
                      padding="none"
                    >
                      {row.stationName}
                    </StyledTableCell>
                    <StyledTableCell className='text-align-override' 
                      component="td"
                      tabIndex={0}
                    >
                      {row.fuelModelCatalogName}
                    </StyledTableCell>
                    <StyledTableCell className='text-align-override'
                      tabIndex={0}

                    >{row.droughtCatalogName}</StyledTableCell>
                    <StyledTableCell className='text-align-override'
                      tabIndex={0}
                    >{row.liveCatalogName}</StyledTableCell>
                    <StyledTableCell className='text-align-override'
                      tabIndex={0}
                    >{row.deadCatalogName}</StyledTableCell>
                    <StyledTableCell className='text-align-override'
                      tabIndex={0}
                    >{moment(row.modifiedTime).format('MM-DD-YYYY')}</StyledTableCell>
                    <StyledTableCell className='text-align-override'
                      tabIndex={0}
                    >{row.status?.toUpperCase()}</StyledTableCell>
                    <StyledTableCell className='text-align-override'
                      tabIndex={0}
                    >{row.modifiedBy}</StyledTableCell>
                  </StyledTableRow>
                );//
              })}
             
            </TableBody>
          </Table>
        </TableContainer>
        <CustomTablePagination
          rowsPerPageOptions={[10, 20, 30]}
          count={totalCount}
          page={page}
          rowsPerPage={rowsPerPage}
          onPageChange={handleChangePage}
          onChangeRowsPerPage={handleChangeRowsPerPage}
        />

    </div>
  );
}